import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw, Save, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { guestService, type HostQuotaSummaryResponse } from '@/lib/api/guest.service';
import { handleApiError } from '@/lib/api/client';
import type { Event } from '@/types/event.types';

interface HostQuotasTabProps {
  celebrationId: number;
  events: Event[];
  canEdit: boolean;
}

const makeQuotaKey = (eventId: number, hostUserId: number): string => `${eventId}:${hostUserId}`;

const getStatusBadgeClass = (status: 'ok' | 'full' | 'over' | 'unlimited') => {
  switch (status) {
    case 'ok':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'full':
      return 'bg-amber-100 text-amber-800 border-amber-200';
    case 'over':
      return 'bg-red-100 text-red-800 border-red-200';
    case 'unlimited':
      return 'bg-slate-100 text-slate-700 border-slate-200';
    default:
      return 'bg-slate-100 text-slate-700 border-slate-200';
  }
};

export const HostQuotasTab: React.FC<HostQuotasTabProps> = ({
  celebrationId,
  events,
  canEdit,
}) => {
  const [summary, setSummary] = useState<HostQuotaSummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
    const { t } = useTranslation(['guests']);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [eventFilter, setEventFilter] = useState<number | null>(null);
  const [hostFilter, setHostFilter] = useState<number | null>(null);
  const [draftAllocatedSeats, setDraftAllocatedSeats] = useState<Record<string, string>>({});
  const [baseAllocatedSeats, setBaseAllocatedSeats] = useState<Record<string, string>>({});

  const loadSummary = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const nextSummary = await guestService.getHostQuotaSummary(celebrationId);
      setSummary(nextSummary);

      const nextDraft: Record<string, string> = {};
      for (const row of nextSummary.eventQuotas) {
        nextDraft[makeQuotaKey(row.eventId, row.hostUserId)] =
          row.allocatedSeats === null ? '' : String(row.allocatedSeats);
      }

      setDraftAllocatedSeats(nextDraft);
      setBaseAllocatedSeats(nextDraft);
    } catch (loadError) {
      setError(handleApiError(loadError) || t('guests:quotas.errors.loadHostQuotas'));
    } finally {
      setLoading(false);
    }
  }, [celebrationId]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const dirtyKeys = useMemo(() => {
    return Object.keys(draftAllocatedSeats).filter((key) => {
      return (draftAllocatedSeats[key] || '') !== (baseAllocatedSeats[key] || '');
    });
  }, [draftAllocatedSeats, baseAllocatedSeats]);

  const filteredEventQuotaRows = useMemo(() => {
    if (!summary) return [];

    return summary.eventQuotas.filter((row) => {
      if (eventFilter !== null && row.eventId !== eventFilter) {
        return false;
      }
      if (hostFilter !== null && row.hostUserId !== hostFilter) {
        return false;
      }
      return true;
    });
  }, [summary, eventFilter, hostFilter]);

  const handleSave = async () => {
    if (!summary || dirtyKeys.length === 0 || !canEdit) {
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSaveMessage(null);

      const payload = dirtyKeys.map((key) => {
        const [eventIdStr, hostUserIdStr] = key.split(':');
        const rawValue = draftAllocatedSeats[key]?.trim() ?? '';

        if (rawValue === '') {
          return {
            event_id: Number(eventIdStr),
            host_user_id: Number(hostUserIdStr),
            allocated_seats: null,
          };
        }

        const parsed = Number(rawValue);
        if (!Number.isInteger(parsed) || parsed < 0) {
          throw new Error(t('guests:quotas.errors.invalidAllocation'));
        }

        return {
          event_id: Number(eventIdStr),
          host_user_id: Number(hostUserIdStr),
          allocated_seats: parsed,
        };
      });

      const updatedSummary = await guestService.upsertEventHostQuotas(celebrationId, payload);
      setSummary(updatedSummary);

      const nextDraft: Record<string, string> = {};
      for (const row of updatedSummary.eventQuotas) {
        nextDraft[makeQuotaKey(row.eventId, row.hostUserId)] =
          row.allocatedSeats === null ? '' : String(row.allocatedSeats);
      }
      setDraftAllocatedSeats(nextDraft);
      setBaseAllocatedSeats(nextDraft);
      setSaveMessage(t('guests:quotas.messages.updateSuccess'));
    } catch (saveError) {
      setError(handleApiError(saveError) || t('guests:quotas.errors.saveEventQuotas'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <RefreshCw className="h-4 w-4 animate-spin" />
            {t('guests:quotas.loading')}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!summary) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-red-700">
          {error || t('guests:quotas.errors.loadFailed')}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ShieldAlert className="h-4 w-4 text-cyan-600" />
            {t('guests:quotas.sections.celebrationSnapshot')}
          </CardTitle>
          <CardDescription>
            {t('guests:quotas.descriptions.quotaGuidance')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-gray-600">{t('guests:quotas.columns.host')}</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-600">{t('guests:quotas.columns.celebrationQuota')}</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-600">{t('guests:quotas.columns.used')}</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-600">{t('guests:quotas.columns.remaining')}</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-600">{t('guests:quotas.columns.status')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {summary.hosts.map((host) => (
                  <tr key={host.hostUserId}>
                    <td className="px-4 py-2">
                      {host.hostName}
                      {host.hostNickname ? (
                        <span className="ml-1 text-xs text-gray-500">({host.hostNickname})</span>
                      ) : null}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {host.celebrationQuota === null ? t('guests:quotas.values.unlimited') : `${host.celebrationQuota}`}
                    </td>
                    <td className="px-4 py-2 text-right">{host.celebrationUsedSeats}</td>
                    <td className="px-4 py-2 text-right">
                      {host.celebrationRemainingSeats === null ? t('guests:quotas.values.unlimited') : `${host.celebrationRemainingSeats}`}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <span
                        className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${
                          host.celebrationStatus === 'warning'
                            ? 'bg-amber-100 text-amber-800 border-amber-200'
                            : host.celebrationStatus === 'ok'
                              ? 'bg-green-100 text-green-800 border-green-200'
                              : 'bg-slate-100 text-slate-700 border-slate-200'
                        }`}
                      >
                        {host.celebrationStatus}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <CardTitle className="text-base">{t('guests:quotas.sections.eventQuotas')}</CardTitle>
              <CardDescription>
                {t('guests:quotas.descriptions.eventQuotas')}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={loadSummary} disabled={loading || saving}>
                <RefreshCw className="mr-1 h-3.5 w-3.5" />
                {t('guests:quotas.actions.refresh')}
              </Button>
              {canEdit && dirtyKeys.length > 0 && (
                <Button size="sm" onClick={handleSave} disabled={saving}>
                  {saving ? (
                    <>
                      <RefreshCw className="mr-1 h-3.5 w-3.5 animate-spin" />
                      {t('guests:quotas.actions.saving')}
                    </>
                  ) : (
                    <>
                      <Save className="mr-1 h-3.5 w-3.5" />
                      {t('guests:quotas.actions.saveQuotas')}
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {events.length === 0 && (
            <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 px-4 py-5 text-sm text-slate-600">
              {t('guests:quotas.empty.events')}
            </div>
          )}

          {!canEdit && (
            <div className="rounded-md border border-cyan-200 bg-cyan-50 px-3 py-2 text-xs text-cyan-800">
              {t('guests:quotas.notices.viewOnly')}
            </div>
          )}

          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {error}
            </div>
          )}

          {saveMessage && (
            <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-700">
              {saveMessage}
            </div>
          )}

          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            <select
              title={t('guests:quotas.tooltips.filterByEvent')}
              value={eventFilter ?? ''}
              onChange={(event) => setEventFilter(event.target.value ? Number(event.target.value) : null)}
              className="w-full rounded-md border border-gray-300 bg-white px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
            >
              <option value="">{t('guests:quotas.filters.allEvents')}</option>
              {events.map((event) => (
                <option key={event.id} value={event.id}>
                  {event.title}
                </option>
              ))}
            </select>

            <select
              title="Filter quota rows by host"
              value={hostFilter ?? ''}
              onChange={(event) => setHostFilter(event.target.value ? Number(event.target.value) : null)}
              className="w-full rounded-md border border-gray-300 bg-white px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
            >
              <option value="">All Guest Managers</option>
              {summary.hosts.map((host) => (
                <option key={host.hostUserId} value={host.hostUserId}>
                  {host.hostNickname ? `${host.hostName} (${host.hostNickname})` : host.hostName}
                </option>
              ))}
            </select>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-gray-600">Event</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-600">Host</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-600">Allocated</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-600">Used</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-600">Remaining</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-600">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {filteredEventQuotaRows.map((row) => {
                  const key = makeQuotaKey(row.eventId, row.hostUserId);
                  const isDirty = (draftAllocatedSeats[key] || '') !== (baseAllocatedSeats[key] || '');

                  return (
                    <tr key={key} className={isDirty ? 'bg-amber-50/60' : ''}>
                      <td className="px-4 py-2">{row.eventTitle}</td>
                      <td className="px-4 py-2">
                        {row.hostName}
                        {row.hostNickname ? (
                          <span className="ml-1 text-xs text-gray-500">({row.hostNickname})</span>
                        ) : null}
                      </td>
                      <td className="px-4 py-2 text-right">
                        {canEdit ? (
                          <Input
                            type="number"
                            min={0}
                            step={1}
                            value={draftAllocatedSeats[key] ?? ''}
                            onChange={(event) => {
                              setSaveMessage(null);
                              setDraftAllocatedSeats((prev) => ({
                                ...prev,
                                [key]: event.target.value,
                              }));
                            }}
                            placeholder="Unlimited"
                            className="ml-auto h-8 w-24 text-right"
                          />
                        ) : (
                          <span>{row.allocatedSeats === null ? t('guests:quotas.values.unlimited') : row.allocatedSeats}</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right">{row.usedSeats}</td>
                      <td className="px-4 py-2 text-right">
                        {row.remainingSeats === null ? t('guests:quotas.values.unlimited') : row.remainingSeats}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${getStatusBadgeClass(row.status)}`}>
                          {row.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
